import { stripToNumbersAndDecimal } from '../functions/number.jsx'
import { fetchAuthSession } from 'aws-amplify/auth';

// No answer came back for a create that had already been sent, so QuickBooks
// may have created it. The screen words this as "check QuickBooks before
// publishing this receipt again".
const UNCERTAIN_NO_ANSWER = {
  stage: 'createTxnUncertain',
  nothingCreated: false,
  errorType: 'NoAnswer',
  message: 'No answer came back from QuickBooks in time.',
  detail: 'No answer came back from QuickBooks in time.',
};

// Turns a non-200 response from the QuickBooks proxy into a failure result
// shaped for mapQbErrorToFriendly ({ ok:false, stage, errorType, message, detail }).
//
// qboProxy's own errors have the body { error: "<message>" }:
//   412  no_realmId_configured / no_tokens_found  (QuickBooks not connected)
//   401  refresh_failed / invalid_grant            (QuickBooks connection expired)
//   400  unknown_operation
//   500  anything else the operation threw
// API Gateway's own errors (expired sign-in, bad route, timeout) have the body
// { message: "..." } instead, so the two can be told apart.
export const describeQboProxyHttpError = (status, errorText) => {
  let parsed = null;
  try {
    parsed = JSON.parse(errorText);
  } catch {
    // not JSON; treated as a gateway error below
  }

  const base = { ok: false, httpStatus: status };
  const reconnect =
    'The QuickBooks connection needs to be reconnected from the Admin dashboard. ' +
    'Nothing was sent to QuickBooks.';

  // A structured result from the proxy, passed through as it is.
  if (parsed && typeof parsed === 'object' &&
      (parsed.ok === false || parsed.stage || parsed.errorType)) {
    return { ...parsed, ...base };
  }

  // The proxy's { error } body.
  if (parsed && typeof parsed === 'object' && typeof parsed.error === 'string') {
    const proxyError = parsed.error;
    if (status === 412 || status === 401 ||
        /no_realmId_configured|no_tokens_found|refresh_failed|invalid_grant/.test(proxyError)) {
      return {
        ...base,
        stage: 'auth',
        errorType: 'QboConnection',
        message: reconnect,
        detail: reconnect,
        proxyError,
      };
    }
    if (proxyError === 'unknown_operation') {
      return {
        ...base,
        stage: 'proxy',
        errorType: 'UnknownOperation',
        message: 'The app and the QuickBooks proxy are out of step. Nothing was sent to QuickBooks.',
        detail: 'The app and the QuickBooks proxy are out of step. Nothing was sent to QuickBooks.',
        proxyError,
      };
    }
    let shown = proxyError.length > 300 ? `${proxyError.slice(0, 300)}…` : proxyError;
    if (!/[.!?…]$/.test(shown)) shown += '.';
    return {
      ...base,
      stage: 'proxy',
      errorType: `Proxy${status}`,
      message: 'QuickBooks returned an error.',
      detail:
        `QuickBooks returned an error: ${shown} ` +
        'Nothing was saved in the app. Check QuickBooks for this expense before publishing again.',
      proxyError,
    };
  }

  // Anything else came from API Gateway or the network path, not the proxy.
  const gatewayMessage = (parsed && typeof parsed.message === 'string') ? parsed.message : (errorText || '');
  // Only a 401 means the sign-in expired. API Gateway also sends 403 for a
  // wrong route ("Missing Authentication Token"), so 403 gets the general text.
  const signInProblem = status === 401;
  const text = signInProblem
    ? 'Your sign-in has expired. Refresh the page, sign in again, and publish again. Nothing was sent to QuickBooks.'
    : `The QuickBooks connection didn't respond normally (HTTP ${status}). Nothing was saved in the app. ` +
      'Check QuickBooks for this expense before publishing again.';
  return {
    ...base,
    stage: 'http',
    errorType: `Http${status}`,
    message: text,
    detail: text,
    gatewayMessage: gatewayMessage.slice(0, 300),
  };
};

export const pushStatementToQuickBooks = async (purchaseObject) => {
  const {
    files,
    identityId,
    qbenv,
    accountId,
    accountRefId,
    vendorId,
    txnAmount,
    expensCatId,
    classId,
    memo,
    txnDate,
    customerId,
    description,
    statementType,
    dueDate,
    invoiceNumber,
    amountDue,
  } = purchaseObject;

  const params = {
    files,
    identityid: identityId,
    qbenv,
    accountId,
    accountRefId,
    vendorId: vendorId.toString(),
    txnAmount: stripToNumbersAndDecimal(txnAmount),
    expensCatId,
    classId: classId ? classId.toString() : '',
    memo,
    txnDate,
    customerId: customerId || '',
    // description: description || '',
    description: description || null,
    statementType: statementType || 'receipt',
    dueDate: dueDate || '',
    invoiceNumber: invoiceNumber || '',
    amountDue: amountDue || '',
  };

  // Dev record mode: when VITE_QBO_RECORD_MODE is 'true' and the publish is
  // going to the sandbox, the QuickBooks proxy builds the request and returns
  // it without sending it. The proxy refuses this for production requests.
  if (import.meta.env.VITE_QBO_RECORD_MODE === 'true' && qbenv === 'sandbox') {
    params.recordOnly = true;
  }

  let sent = false;
  try {
    console.log('Request params:', params);

    const session = await fetchAuthSession();
    const authToken = `Bearer ${session.tokens.idToken}`;

    const API_ENDPOINT = `${import.meta.env.VITE_API_BASE_URL}/qboproxy`;

    sent = true;
    const response = await fetch(API_ENDPOINT,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: authToken,
        },
        body: JSON.stringify({
          operation: 'createQBPurchase',
          appEnv: import.meta.env.VITE_APP_ENV || 'production',
          qbenv,
          app: 'shared',
          payload: params,
        }),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Response status:', response.status);
      console.error('Response body:', errorText);

      // Return a failure result instead of throwing, so the caller's
      // mapQbErrorToFriendly can word it. The caller treats anything without
      // ok === true as a failure and writes nothing to the database.
      const described = describeQboProxyHttpError(response.status, errorText);
      // 502/503/504 come from the gateway or router giving up while waiting.
      // The proxy never sends these itself, so the create may have gone through.
      if ([502, 503, 504].includes(response.status)) {
        return { ...described, ...UNCERTAIN_NO_ANSWER };
      }
      return described;
    }

    const dataJSON = await response.json();
    console.log(' 👀 Response data:', dataJSON);
    return dataJSON;
  } catch (error) {
    console.error('Full error:', error);
    // The request had already gone out (lost connection, timeout), so
    // QuickBooks may have created the purchase even though no answer arrived.
    if (sent) {
      return { ok: false, ...UNCERTAIN_NO_ANSWER };
    }
    throw error;
  }
};